import * as React from 'react';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import type {FilterObject} from 'nutshell-core/types';
import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {Button} from 'shells/button/button';
import {PageControlBar} from 'shells/control-bar/page-control-bar';
import {EditIcon, EnvelopeIcon, ReportsIcon, AudienceIcon} from 'shells/icon';
import {SavedSettingToast} from 'shells/saved-setting-toast/saved-setting-toast';
import {SectionLabel} from 'shells/typography/section-label';

import type {
    EditionUpdateInput,
    EditionFragment as DripEdition,
    EditionStubFragment as DripEditionStub,
} from 'nutshell-graphql-types';

import {useGetSessionUser} from '../../../core/use-get-session-user';
import {PageContainer, PageTitleBar} from '../../../layout';
import type {ReportURLParams} from '../../../reports/report-types';

import {getEditionStatusBarInfo} from '../../editions/edition-helpers';
import {EditionEngagement} from '../../editions/engagement/edition-engagement';
import {EditionRecipientEngagementFilter} from '../../editions/recipient-list/edition-recipient-engagement-filter';
import {EditionRecipientList} from '../../editions/recipient-list/edition-recipient-list';
import {
    adjustFiltersForComparisonEdition,
    editionFieldFilterFunction,
} from '../../editions/recipient-list/helpers';
import {SendTestModal} from '../../editions/send-test-modal/send-test-modal';
import {MarketingStatsBanner} from '../../marketing-stats-banner/marketing-stats-banner';

import {DripEditionsDetailsForm} from './drip-editions-details-form';
import {DripSequenceEditionTabContent} from './drip-sequence-edition-tab-content';
import {DripSequenceMiniMapSidebar} from './drip-sequence-mini-map-sidebar';

import styles from './drip-sequence-edition-page.module.css';

type OwnProps = ContextRouter;

type Props = OwnProps & {
    edition: DripEdition | null | undefined;
    editionStub: DripEditionStub | null | undefined;
    editionsList: DripEditionStub[];
    errorLoadingEditionsList: string | null | undefined;
    isLoading: boolean;
    isLoadingEditionsList: boolean;
    isSaving: boolean;
    onSendEmail: (toEmailAddress: string[]) => Promise<any>;
    onChange: (editionId: string, beeBodyJson: string) => Promise<any>;
    onEditionSave: (saveData: EditionUpdateInput) => Promise<any>;
    dripSequenceId: string;
    editionId: string;
    onSetSelectedEditorTab: (arg1: string) => void;
    generatePath: (arg1: string) => string;
    onEditionChange: (activeTab: string, newEditionId: string) => void;
    onCreateEdition: (name: string, campaignId: string) => Promise<any>;
    onDuplicateEdition: (editionId: string, campaignId: string, name: string) => Promise<any>;
    activeFilters: FilterObject[];
    onParamChange: (arg1: any, arg2: boolean) => any;
    onParamPush: (arg1: any, arg2: boolean) => any;
    urlParams: ReportURLParams<'contacts'>;
    onRemoveAllFilters: () => void;

    onToggleEditMode: () => void;
};

export function DripEditionMiniMapMode(props: Props) {
    const [shouldShowSendTestModal, setShouldShowSendTestModal] = React.useState<boolean>(false);
    const [isSendingTestEmail, setIsSendingTestEmail] = React.useState<boolean>(false);
    const [isSendingTestEmailComplete, setIsSendingTestEmailComplete] =
        React.useState<boolean>(false);
    const [testEmailErrorMessage, setTestEmailErrorMessage] = React.useState<
        string | null | undefined
    >(null);

    const savedTimeoutRef = React.useRef();
    const [valueSaved, setValueSaved] = React.useState<boolean>(false);

    const {user} = useGetSessionUser();

    // @ts-expect-error
    function updateEditionDetails(saveData) {
        return props.onEditionSave(saveData).then(() => {
            setValueSaved(true);

            if (savedTimeoutRef.current) {
                clearTimeout(savedTimeoutRef.current);
            }

            // @ts-expect-error
            savedTimeoutRef.current = setTimeout(() => {
                setValueSaved(false);
            }, 1000);
        });
    }

    const edition = props.edition || props.editionStub;
    const activeTab = props.match.params.tab ? props.match.params.tab : 'editor'; // default to editor

    const isEditionDraft = edition && edition.status === 'NEW';
    const sentStatuses = ['SENDING', 'SENT', 'HALTED', 'IN_REVIEW'];
    const isEditionSent = edition && sentStatuses.includes(edition.status);
    const isEditionCanceled =
        edition &&
        (edition.status === 'CANCELLED' ||
            edition.status === 'RETIRING' ||
            edition.status === 'RETIRED');

    const adjustedFilters: FilterObject[] =
        edition && edition.comparisonEdition
            ? adjustFiltersForComparisonEdition(
                  props.activeFilters,
                  edition.comparisonEdition.id,
                  props.urlParams.version
              )
            : props.activeFilters;

    const onSetSelectedEditorTab = props.onSetSelectedEditorTab;
    React.useEffect(() => {
        if (activeTab === 'editor' && (isEditionSent || isEditionCanceled)) {
            onSetSelectedEditorTab('engagement');
        }
    }, [activeTab, isEditionSent, isEditionCanceled, onSetSelectedEditorTab]);

    const tabs = [];

    // Only show the editor tab if the edition is in draft state
    if (!isEditionSent && !isEditionCanceled) {
        tabs.push({
            id: 'editor',
            label: 'Editor',
            icon: EditIcon,
            href: props.generatePath('editor'),
        });
    }

    const links = edition && edition.emailTemplate ? edition.emailTemplate.links : [];
    const urls = links
        ? links
              // @ts-expect-error
              .filter((link): boolean => Boolean(link.url))
              // @ts-expect-error
              .map((link) => {
                  // Flow now knows link.url is non-null here
                  return link.url;
              })
        : [];

    tabs.push(
        {
            id: 'details',
            label: 'Details',
            icon: EnvelopeIcon,
            href: props.generatePath('details'),
            component: edition && (
                <div className={styles['details-container']}>
                    <SectionLabel>
                        <SavedSettingToast state={valueSaved ? 'complete' : 'initial'}>
                            Message details
                        </SavedSettingToast>
                    </SectionLabel>
                    {props.edition ? (
                        <DripEditionsDetailsForm
                            edition={props.edition}
                            onEditionSave={(saveData) => {
                                // Local method to handle a saveTimeout, will make network call
                                return updateEditionDetails(saveData);
                            }}
                            isReadOnly={Boolean(
                                !props.edition ||
                                    props.edition.status === 'SENDING' ||
                                    isEditionCanceled
                            )}
                        />
                    ) : undefined}
                </div>
            ),
        },
        {
            id: 'engagement',
            label: 'Engagement',
            icon: ReportsIcon,
            href: props.generatePath('engagement'),
            component: edition && (
                <EditionEngagement
                    // @ts-expect-error
                    activeFilters={props.activeFilters}
                    onParamChange={props.onParamChange}
                    onParamPush={props.onParamPush}
                    urlParams={props.urlParams}
                    edition={edition}
                    neverShowSendingState={true}
                />
            ),
        }
    );

    // Show the recipients tab unless the edition is a draft
    if (!isEditionDraft) {
        tabs.push({
            id: 'recipients',
            label: 'Recipients',
            icon: AudienceIcon,
            href: props.generatePath('recipients'),
            component: edition && (
                <EditionRecipientList
                    activeFilters={props.activeFilters}
                    onParamChange={props.onParamChange}
                    urlParams={props.urlParams}
                    onRemoveAllFilters={props.onRemoveAllFilters}
                    edition={props.edition}
                    campaignType='DRIP_SEQUENCE'
                    graduatedFilters={
                        <EditionRecipientEngagementFilter
                            editionId={edition.id}
                            comparisonEditionId={
                                edition.comparisonEdition && edition.comparisonEdition.id
                            }
                            comparisonWinnerEditionId={
                                edition.comparison &&
                                edition.comparison.winnerEdition &&
                                edition.comparison.winnerEdition.id
                            }
                            // Note, this is the prop, not the filtered filters.
                            // We want to reflect the "default" filter here
                            filters={adjustedFilters}
                            onParamChange={props.onParamChange}
                            urlParams={props.urlParams}
                            urls={urls}
                            createdTime={edition.createdTime}
                        />
                    }
                    fieldFilterFunction={editionFieldFilterFunction}
                />
            ),
        });
    }

    const isDraft = edition && edition.status === 'NEW';
    const isDripActive = edition && edition.status === 'SENDING';

    const getButtons = () => {
        if (isDripActive && !isEditionCanceled) {
            return (
                <Button
                    variant='primary'
                    size='big'
                    onClick={() => {
                        props.onToggleEditMode();
                    }}
                >
                    Edit message
                </Button>
            );
        }

        if (!isEditionCanceled) {
            return (
                <Button
                    variant='primary'
                    size='big'
                    onClick={() => {
                        setShouldShowSendTestModal(true);
                    }}
                >
                    Send a test email
                </Button>
            );
        }

        return null;
    };

    const isOwner = Boolean(
        user &&
            edition &&
            edition.campaign &&
            edition.campaign.owner &&
            edition.campaign.owner.id === user.id
    );

    return (
        <div className={styles['outer-container']}>
            <DripSequenceMiniMapSidebar
                selectedEditionId={props.editionId}
                dripSequenceId={props.dripSequenceId}
                editions={props.editionsList}
                error={props.errorLoadingEditionsList}
                isLoading={props.isLoadingEditionsList}
                // @ts-expect-error
                activeTab={activeTab}
                onEditionChange={(editionId: string) => {
                    props.onEditionChange(activeTab, editionId);
                }}
                onUpdateEdition={({editionId, delay}) => {
                    return props.onEditionSave({editionId: editionId, sendDelay: delay});
                }}
                canAddNewEdition={Boolean(isDraft)}
                campaignId={props.dripSequenceId}
                editionsList={props.editionsList}
                onCreateEdition={props.onCreateEdition}
                onDuplicateEdition={props.onDuplicateEdition}
            />
            <div className={styles['inner-container']}>
                <PageContainer
                    title={edition ? edition.name : 'Message'}
                    pageTitleBar={
                        <PageTitleBar
                            title={edition ? edition.name : 'Message'}
                            subheaderPrimaryText='Edit your drip sequence'
                            noMaxWidth={true}
                            breadcrumbs={[
                                {
                                    to: '/email-marketing/drip-sequences',
                                    text: 'Drip sequences',
                                },
                                {
                                    to: edition ? edition.campaign.htmlUrlPath : '#',
                                    text: edition ? edition.campaign.name : 'Campaign',
                                },
                            ]}
                            editInPlaceProps={{
                                onChange: (newValue: string | null | undefined) =>
                                    edition
                                        ? props.onEditionSave({
                                              editionId: edition.id,
                                              name: newValue ? newValue : '',
                                          })
                                        : undefined,
                                placeholder: 'Name this drip sequence',
                            }}
                            buttons={getButtons()}
                        />
                    }
                    controlBar={
                        <PageControlBar
                            statusInfo={
                                props.edition
                                    ? getEditionStatusBarInfo(
                                          props.edition,
                                          Boolean(user && user.permissions.canAccessFullCampaigns),
                                          undefined,
                                          props.isSaving,
                                          isOwner
                                      )
                                    : undefined
                            }
                            tabs={{
                                selectedTab: activeTab || 'editor',
                                options: tabs,
                            }}
                        />
                    }
                >
                    <MarketingStatsBanner />
                    <DripSequenceEditionTabContent
                        activeTab={activeTab}
                        tabOptions={tabs}
                        edition={props.edition}
                        isLoading={props.isLoading}
                        // @ts-expect-error
                        isValueSaved={valueSaved}
                        onEditionDetailFormSave={(saveData: EditionUpdateInput) => {
                            // Local method to handle a saveTimeout, will make network call
                            return updateEditionDetails(saveData);
                        }}
                        onBeeEditorContentChange={(editionId, json) => {
                            return props.onChange(editionId, json);
                        }}
                        canMakeChanges={Boolean(
                            props.edition &&
                                props.edition.status !== 'SENDING' &&
                                !isEditionCanceled
                        )}
                    />
                    <SendTestModal
                        isOpen={shouldShowSendTestModal}
                        onClose={() => {
                            setShouldShowSendTestModal(false);
                            setIsSendingTestEmail(false);
                            setIsSendingTestEmailComplete(false);
                        }}
                        // @ts-expect-error
                        onSend={(emailAddresses) => {
                            setIsSendingTestEmail(true);
                            props
                                .onSendEmail(emailAddresses)
                                .then(() => {
                                    setIsSendingTestEmail(false);
                                    setIsSendingTestEmailComplete(true);
                                })
                                // @ts-expect-error
                                .catch((err) => {
                                    setIsSendingTestEmail(false);
                                    if (err && err.message) {
                                        setTestEmailErrorMessage(safelyGetGraphQLErrorMessage(err));
                                    } else {
                                        setTestEmailErrorMessage(
                                            'Sorry, something went wrong. Unable to send a test'
                                        );
                                    }
                                });
                        }}
                        isSending={isSendingTestEmail}
                        isSendingComplete={isSendingTestEmailComplete}
                        testEmailErrorMessage={testEmailErrorMessage}
                        onHideTestEmailErrorMessage={() => setTestEmailErrorMessage(null)}
                    />
                </PageContainer>
            </div>
        </div>
    );
}
